"use client";

/**
 * The header notification bell (tdd-0060 §5).
 *
 * Mounted once in `AppHeader`, which every app renders through `RootLayout` —
 * so this reaches all thirty-odd apps with one change rather than thirty. That
 * is the reason tdd-0060 picked the header as the mount point.
 *
 * Renders nothing at all when signed out. An empty bell for an anonymous
 * visitor is a control that cannot do anything, and every request behind it
 * would 401.
 */

import * as React from "react";
import { ArrowUpRight, Bell, Check, Settings, X } from "lucide-react";
import { stageApexHostname } from "@workspace/stage-config/detect";
import { useAuth } from "@workspace/ui/hooks/useAuth";
import { useNotifications, type Notification } from "@workspace/ui/hooks/useNotifications";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";

/** Relative time, to the granularity anyone actually reads. */
function timeAgo(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const levelAccent: Record<Notification["level"], string> = {
  info: "bg-muted-foreground/40",
  action: "bg-primary",
  urgent: "bg-destructive",
};

/** Title and body. `linked` marks the title as the way through to the action. */
function NotificationText({
  notification,
  unread,
  linked = false,
}: {
  notification: Notification;
  unread: boolean;
  linked?: boolean;
}) {
  return (
    <>
      <p
        className={cn(
          "flex items-center gap-1",
          unread ? "font-medium" : "text-muted-foreground",
          linked && "group-hover/action:underline underline-offset-2",
        )}
      >
        <span className="truncate">{notification.title}</span>
        {linked && (
          <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
      </p>
      {notification.body && (
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{notification.body}</p>
      )}
    </>
  );
}

function NotificationRow({
  notification,
  onRead,
  onDismiss,
}: {
  notification: Notification;
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  const unread = notification.readAt === null;

  return (
    <div
      className={cn(
        "relative flex gap-2 px-3 py-2.5 text-sm",
        unread ? "bg-accent/40" : "bg-transparent",
      )}
    >
      {/* Level as a colour bar rather than an icon: it reads at a glance down a
          list, and does not compete with the dismiss control for the same
          corner. */}
      <span
        aria-hidden="true"
        className={cn("mt-1 h-full w-0.5 shrink-0 rounded-full", levelAccent[notification.level])}
      />

      <div className="min-w-0 flex-1">
        {notification.action ? (
          // The whole message is the link, not a small label after the time:
          // the message is what someone reads and reaches for. The label
          // becomes its accessible description and tooltip.
          //
          // Acting on a notification IS reading it — requiring a separate
          // "mark read" after clicking through would leave the badge claiming
          // work that is already done.
          //
          // A new tab, not this one. The bell is opened from wherever the user
          // already was — mid-conversation, mid-edit — and a notification is
          // an aside, not a destination. Navigating the current tab away to
          // act on one costs them the thing they were actually doing.
          //
          // A plain anchor rather than `next/link`: every action href is an
          // absolute cross-app URL (the sync page, a canvas), so there is no
          // client-side route to prefetch, and `target` on a Link would be
          // doing nothing but adding indirection.
          //
          // `noopener` because the opened page gets a `window.opener` handle
          // back into this one otherwise.
          <a
            href={notification.action.href}
            target="_blank"
            rel="noopener noreferrer"
            title={notification.action.label}
            aria-description={notification.action.label}
            onClick={() => onRead(notification.id)}
            className="group/action -m-1 block rounded p-1 hover:bg-accent/60 focus-visible:outline-2 focus-visible:outline-ring"
          >
            <NotificationText notification={notification} unread={unread} linked />
          </a>
        ) : (
          <NotificationText notification={notification} unread={unread} />
        )}

        <p className="mt-1.5 text-xs text-muted-foreground">{timeAgo(notification.createdAt)}</p>
      </div>

      <div className="flex shrink-0 flex-col gap-1">
        {unread && (
          <button
            onClick={() => onRead(notification.id)}
            aria-label="Mark as read"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Check className="size-3.5" />
          </button>
        )}
        <button
          onClick={() => onDismiss(notification.id)}
          aria-label="Dismiss"
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

/**
 * Link to push settings on the stage's apex app.
 *
 * Push lives on ONE origin: `web` at the apex is the platform's only
 * installable app, so it holds the user's subscription and receives every
 * app's notifications (a subscription is scoped to the origin it was made on).
 * Every other app's bell therefore points there rather than offering a
 * subscribe button of its own — a second installable origin would mean a
 * second subscription and every notification arriving twice.
 *
 * Rendered inside the dropdown content, which only mounts once opened, so
 * `window` is always present.
 */
function HubSettingsLink() {
  const apex = stageApexHostname(window.location.hostname);
  if (!apex) return null;

  const onHub = window.location.hostname === apex;
  const className =
    "flex items-center gap-1.5 border-t px-3 py-2 text-xs text-muted-foreground hover:text-foreground";

  // On the hub itself this is an ordinary in-app navigation. Anywhere else it
  // is a different origin, so a new tab, as with notification actions above.
  return onHub ? (
    <a href="/settings/notifications" className={className}>
      <Settings className="size-3.5" aria-hidden="true" />
      Notification settings
    </a>
  ) : (
    <a
      href={`https://${apex}/settings/notifications`}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <Settings className="size-3.5" aria-hidden="true" />
      Notification settings
    </a>
  );
}

export function NotificationBell({ className }: { className?: string }) {
  const { authenticated } = useAuth();
  const { counts, notifications, loading, load, markSeen, markRead, dismiss } =
    useNotifications(authenticated);
  const [open, setOpen] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        // Fetch on open, not on mount: the list is only ever looked at here,
        // and every app on the platform mounts this component.
        void load();
        // Seen, not read. Opening the panel means "I have noticed these", which
        // kills the dot; it does not mean the work is dealt with, so the unread
        // count survives.
        void markSeen();
      }
    },
    [load, markSeen],
  );

  // Signed out: render nothing rather than a bell that cannot do anything.
  if (!authenticated) return null;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className={cn(
            "relative rounded-full",
            // Nothing waiting: recede, so the bell only draws the eye when it
            // has something to say. Hover and the open panel bring it back.
            counts.unread === 0 &&
              "text-muted-foreground/70 data-[state=open]:text-foreground",
            className,
          )}
          aria-label={
            counts.unread > 0 ? `Notifications (${counts.unread} unread)` : "Notifications"
          }
        >
          <Bell className="size-4" aria-hidden="true" />
          {counts.unread > 0 && (
            <span
              aria-hidden="true"
              className={cn(
                "absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center",
                "rounded-full px-1 text-[10px] font-semibold leading-4 tabular-nums",
                "bg-primary text-primary-foreground",
                // Unseen means something arrived that the user has not yet
                // looked at — worth a ring. Once seen, the count stays but
                // stops shouting.
                counts.unseen > 0 && "ring-2 ring-background",
              )}
            >
              {counts.unread > 9 ? "9+" : counts.unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Notifications</span>
          {counts.unread > 0 && (
            <button
              onClick={() => void markRead()}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-96 divide-y overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : notifications.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Nothing needs your attention.
            </p>
          ) : (
            notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onRead={(id) => void markRead([id])}
                onDismiss={(id) => void dismiss(id)}
              />
            ))
          )}
        </div>

        <HubSettingsLink />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
